import React, { useState } from 'react';
import { Activity, Upload, X, Crown, Menu, LogOut, Shield, Building } from 'lucide-react';
import { LocationSelector } from '../Location/LocationSelector';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { useTeam } from '../../contexts/TeamContext';
import { AdminConsole } from '../Admin/AdminConsole';
import { OrganizationPanel } from '../Organization/OrganizationPanel';

interface HeaderProps {
  fileName: string | null;
  onUploadClick: () => void;
  onClear: () => void;
  userEmail?: string;
  plan?: string;
  onUpgradeClick?: () => void;
  onLogout?: () => void;
}

// Check if current user is superadmin
const isSuperadmin = () => localStorage.getItem('ltss_superadmin') === 'true';

// Check if user has paid plan (pro or enterprise)
const hasPaidPlan = () => {
  const subscription = localStorage.getItem('ltss_subscription');
  if (subscription) {
    try {
      const sub = JSON.parse(subscription);
      return sub.plan === 'enterprise' || sub.plan === 'pro';
    } catch { return false; }
  }
  return false;
};

export function Header({ fileName, onUploadClick, onClear, userEmail, plan, onUpgradeClick, onLogout }: HeaderProps) {
  const { isEnterprise } = usePlanFeatures();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { canManageTeam } = useTeam(); // Keep for future use
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAdminConsole, setShowAdminConsole] = useState(false);
  const [showOrgPanel, setShowOrgPanel] = useState(false);
  
  const superadmin = isSuperadmin();
  const paidUser = hasPaidPlan() && !superadmin; // Paid users who are NOT superadmin
  
  return (
    <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo and Title - Clickable to go back to dashboard */}
          {fileName ? (
            <button 
              onClick={onClear}
              className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity"
              title="Back to Dashboard"
            >
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-base sm:text-lg font-bold gradient-text">LTSS Analytics</h1>
                <p className="text-[10px] sm:text-xs text-slate-400 hidden sm:block">Venus Health Care Solutions</p>
              </div>
            </button>
          ) : (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-base sm:text-lg font-bold gradient-text">LTSS Analytics</h1>
                <p className="text-[10px] sm:text-xs text-slate-400 hidden sm:block">Venus Health Care Solutions</p>
              </div>
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-3">
            {/* File indicator */}
            {fileName && (
              <div className="flex items-center gap-2 bg-slate-700/50 px-3 py-1.5 rounded-lg max-w-[200px]">
                <span className="text-sm text-slate-300 truncate">{fileName}</span>
                <button
                  onClick={onClear}
                  className="text-slate-400 hover:text-red-400 transition-colors flex-shrink-0"
                  title="Clear data"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* Upload Button */}
            <button
              onClick={onUploadClick}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium shadow-lg shadow-blue-600/20"
            >
              <Upload className="w-4 h-4" />
              Upload Report
            </button>

            {/* Location Selector - Enterprise only */}
            {isEnterprise && <LocationSelector />}

            {/* User Controls */}
            {userEmail && (
              <div className="flex items-center gap-3 ml-2 pl-3 border-l border-slate-700">
                <span className="text-xs text-slate-400 hidden lg:inline max-w-[150px] truncate">
                  {userEmail}
                </span>
                
                {/* Admin Console Button - Superadmin only */}
                {superadmin && (
                  <button
                    onClick={() => setShowAdminConsole(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 shadow-lg shadow-amber-500/20 transition-all"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Admin
                  </button>
                )}
                
                {/* Organization Panel Button - Paid users */}
                {paidUser && (
                  <button
                    onClick={() => setShowOrgPanel(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 shadow-lg shadow-purple-500/20 transition-all"
                  >
                    <Building className="w-3.5 h-3.5" />
                    Organization
                  </button>
                )}
                
                {onUpgradeClick && !superadmin && !paidUser && (
                  <button
                    onClick={onUpgradeClick}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      plan === 'free'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 shadow-lg shadow-purple-500/20'
                        : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                    }`}
                  >
                    <Crown className="w-3.5 h-3.5" />
                    {plan === 'free' ? 'Upgrade' : plan}
                  </button>
                )}
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="text-xs text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign out
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="flex sm:hidden items-center gap-2">
            {/* Upload Button - Always visible */}
            <button
              onClick={onUploadClick}
              className="flex items-center justify-center w-9 h-9 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              title="Upload Report"
            >
              <Upload className="w-4 h-4" />
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex items-center justify-center w-9 h-9 bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-slate-700 py-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
            {/* Current File */}
            {fileName && (
              <div className="flex items-center justify-between px-2 py-2 bg-slate-700/30 rounded-lg">
                <span className="text-sm text-slate-300 truncate flex-1">{fileName}</span>
                <button
                  onClick={() => { onClear(); setMobileMenuOpen(false); }}
                  className="text-slate-400 hover:text-red-400 transition-colors ml-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* User Info */}
            {userEmail && (
              <div className="px-2 py-2 text-xs text-slate-400 border-b border-slate-700/50 pb-3">
                Signed in as <span className="text-slate-300">{userEmail}</span>
              </div>
            )}
            
            {/* Menu Items */}
            <div className="space-y-1">
              {/* Admin Console - Mobile (Superadmin only) */}
              {superadmin && (
                <button
                  onClick={() => { setShowAdminConsole(true); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400"
                >
                  <Shield className="w-4 h-4" />
                  Admin Console
                </button>
              )}
              
              {/* Organization Panel - Mobile (Paid users) */}
              {paidUser && (
                <button
                  onClick={() => { setShowOrgPanel(true); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400"
                >
                  <Building className="w-4 h-4" />
                  Organization
                </button>
              )}
              
              {onUpgradeClick && !superadmin && !paidUser && (
                <button
                  onClick={() => { onUpgradeClick(); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    plan === 'free'
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400'
                      : 'text-slate-300 hover:bg-slate-700/50'
                  }`}
                >
                  <Crown className="w-4 h-4" />
                  {plan === 'free' ? 'Upgrade Plan' : `Current Plan: ${plan}`}
                </button>
              )}
              
              {isEnterprise && (
                <div className="px-3 py-2">
                  <LocationSelector />
                </div>
              )}
              
              {onLogout && (
                <button
                  onClick={() => { onLogout(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Admin Console - Superadmin only */}
      {showAdminConsole && (
        <AdminConsole 
          onClose={() => setShowAdminConsole(false)}
        />
      )}
      
      {/* Organization Panel - Paid users */}
      {showOrgPanel && (
        <OrganizationPanel 
          onClose={() => setShowOrgPanel(false)}
          onUpgrade={onUpgradeClick}
        />
      )}
    </header>
  );
}
